import { mockLocations, mockPurchaseOrders, mockShipments } from '@/lib/mock-data';
import { mockLocationOrders } from '@/lib/mock-data-components';
import {
  isLaunchInUpcomingWindow,
  isRelevantLaunch,
  launchMatchesOriginalDates,
  sortLaunchesByInstallationDate,
} from '@/lib/utils/location-launches';
import type { Location } from '@/types/location';
import type { LocationLaunch, LocationLaunchInsert, LocationLaunchUpdate } from '@/types/location-launches';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const mockLocationLaunches: LocationLaunch[] = [];

function buildPrimaryLaunchFromLocation(location: Location): LocationLaunch {
  return {
    id: `launch-primary-${location.id}`,
    location_id: location.id,
    status: location.status === 'cancelled' || location.deal_status === 'cancelled'
      ? 'cancelled'
      : location.installation_status,
    installation_date: location.installation_date,
    launch_date: location.launch_date,
    installation_type: location.installation_type ?? null,
    is_primary: true,
    excluded_from_upcoming_launches: location.excluded_from_upcoming_launches,
    notes: 'Backfilled from location record',
    created_at: location.created_at,
    updated_at: location.updated_at,
    created_by: location.created_by,
    updated_by: location.updated_by,
  };
}

function ensureSeeded(): void {
  for (const location of mockLocations) {
    if (!mockLocationLaunches.some((launch) => launch.location_id === location.id && launch.is_primary)) {
      mockLocationLaunches.push(buildPrimaryLaunchFromLocation(location));
    }
  }

  for (const order of mockLocationOrders) {
    if (!order.launch_id) {
      order.launch_id = `launch-primary-${order.location_id}`;
    }
  }

  for (const purchaseOrder of mockPurchaseOrders) {
    if (purchaseOrder.location_id && !purchaseOrder.launch_id) {
      purchaseOrder.launch_id = `launch-primary-${purchaseOrder.location_id}`;
    }
  }

  for (const shipment of mockShipments) {
    if (shipment.location_id && !shipment.launch_id) {
      shipment.launch_id = `launch-primary-${shipment.location_id}`;
    }
  }
}

export function ensureMockPrimaryLaunch(location: Location): LocationLaunch {
  ensureSeeded();
  const existing = mockLocationLaunches.find((launch) => launch.location_id === location.id && launch.is_primary);
  if (existing) {
    return existing;
  }

  const primaryLaunch = buildPrimaryLaunchFromLocation(location);
  mockLocationLaunches.push(primaryLaunch);
  return primaryLaunch;
}

export function syncMockPrimaryLaunchFromLocation(location: Location): void {
  ensureSeeded();
  const primaryLaunch = ensureMockPrimaryLaunch(location);
  primaryLaunch.status =
    location.status === 'cancelled' || location.deal_status === 'cancelled'
      ? 'cancelled'
      : location.installation_status;
  primaryLaunch.installation_date = location.installation_date;
  primaryLaunch.launch_date = location.launch_date;
  primaryLaunch.installation_type = location.installation_type ?? null;
  primaryLaunch.excluded_from_upcoming_launches = location.excluded_from_upcoming_launches;
  primaryLaunch.updated_at = location.updated_at;
  primaryLaunch.updated_by = location.updated_by;
}

export function deleteMockLaunchesForLocation(locationId: string): void {
  for (let index = mockLocationLaunches.length - 1; index >= 0; index -= 1) {
    if (mockLocationLaunches[index]?.location_id === locationId) {
      mockLocationLaunches.splice(index, 1);
    }
  }
}

export const mockLocationLaunchesService = {
  async getAll(): Promise<LocationLaunch[]> {
    await delay(300);
    ensureSeeded();
    return [...mockLocationLaunches].sort(sortLaunchesByInstallationDate);
  },

  async getById(id: string): Promise<LocationLaunch | null> {
    await delay(200);
    ensureSeeded();
    return mockLocationLaunches.find((launch) => launch.id === id) ?? null;
  },

  async getByLocationId(locationId: string): Promise<LocationLaunch[]> {
    await delay(200);
    ensureSeeded();
    return mockLocationLaunches
      .filter((launch) => launch.location_id === locationId)
      .sort(sortLaunchesByInstallationDate);
  },

  async getPrimaryByLocationId(locationId: string): Promise<LocationLaunch | null> {
    await delay(200);
    ensureSeeded();
    return mockLocationLaunches.find((launch) => launch.location_id === locationId && launch.is_primary) ?? null;
  },

  async getUpcomingWindow(): Promise<LocationLaunch[]> {
    await delay(200);
    ensureSeeded();
    return mockLocationLaunches.filter((launch) => isLaunchInUpcomingWindow(launch));
  },

  async getRelevantByLocation(locationId: string): Promise<LocationLaunch[]> {
    await delay(200);
    ensureSeeded();
    return mockLocationLaunches
      .filter((launch) => launch.location_id === locationId)
      .filter((launch) => isRelevantLaunch(launch))
      .sort(sortLaunchesByInstallationDate);
  },

  async create(data: LocationLaunchInsert): Promise<LocationLaunch> {
    await delay(400);
    ensureSeeded();

    const newLaunch: LocationLaunch = {
      id: `launch-${Date.now()}`,
      location_id: data.location_id,
      status: data.status ?? 'installation_pending',
      installation_date: data.installation_date ?? null,
      launch_date: data.launch_date ?? null,
      installation_type: data.installation_type ?? null,
      is_primary: data.is_primary ?? false,
      excluded_from_upcoming_launches: data.excluded_from_upcoming_launches ?? false,
      notes: data.notes ?? null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: null,
      updated_by: null,
    };

    mockLocationLaunches.push(newLaunch);
    return newLaunch;
  },

  async ensurePrimaryForLocation(input: {
    locationId: string;
    status: LocationLaunch['status'];
    installationDate?: string | null;
    launchDate?: string | null;
    installationType?: LocationLaunch['installation_type'];
    excludedFromUpcoming?: boolean;
    notes?: string | null;
  }): Promise<LocationLaunch> {
    await delay(100);
    ensureSeeded();
    const existing = mockLocationLaunches.find(
      (launch) => launch.location_id === input.locationId && launch.is_primary
    );
    if (existing) {
      return existing;
    }

    return this.create({
      location_id: input.locationId,
      status: input.status,
      installation_date: input.installationDate ?? null,
      launch_date: input.launchDate ?? null,
      installation_type: input.installationType ?? null,
      is_primary: true,
      excluded_from_upcoming_launches: input.excludedFromUpcoming ?? false,
      notes: input.notes ?? 'Primary launch',
    });
  },

  async update(id: string, data: LocationLaunchUpdate): Promise<LocationLaunch> {
    await delay(400);
    ensureSeeded();
    const index = mockLocationLaunches.findIndex((launch) => launch.id === id);
    if (index === -1) {
      throw new Error('Location launch not found');
    }

    const existing = mockLocationLaunches[index];
    if (!existing) {
      throw new Error('Location launch not found');
    }

    const updated: LocationLaunch = {
      ...existing,
      ...data,
      updated_at: new Date().toISOString(),
    };
    mockLocationLaunches[index] = updated;
    return updated;
  },

  async setPrimary(locationId: string, launchId: string): Promise<LocationLaunch> {
    await delay(150);
    ensureSeeded();

    const targetLaunch = mockLocationLaunches.find(
      (launch) => launch.location_id === locationId && launch.id === launchId
    );
    if (!targetLaunch) {
      throw new Error('Location launch not found');
    }

    for (const launch of mockLocationLaunches) {
      if (launch.location_id === locationId) {
        launch.is_primary = launch.id === launchId;
        launch.updated_at = new Date().toISOString();
      }
    }

    return targetLaunch;
  },

  async restoreOriginalPrimaryForLocation(input: {
    locationId: string;
    status: LocationLaunch['status'];
    installationDate: string | null;
    launchDate: string | null;
    installationType?: LocationLaunch['installation_type'];
    excludedFromUpcoming?: boolean;
    notes?: string | null;
  }): Promise<LocationLaunch> {
    await delay(150);
    ensureSeeded();

    if (!input.installationDate && !input.launchDate) {
      throw new Error('The location does not have original installation or launch dates to restore.');
    }

    const matchingLaunch =
      mockLocationLaunches.find(
        (launch) =>
          launch.location_id === input.locationId &&
          launchMatchesOriginalDates(launch, input.installationDate, input.launchDate)
      ) ?? null;

    if (matchingLaunch) {
      await this.setPrimary(input.locationId, matchingLaunch.id);
      return this.update(matchingLaunch.id, {
        status: input.status,
        installation_date: input.installationDate,
        launch_date: input.launchDate,
        installation_type: input.installationType ?? null,
        excluded_from_upcoming_launches: input.excludedFromUpcoming ?? false,
      });
    }

    const currentPrimary = mockLocationLaunches.find(
      (launch) => launch.location_id === input.locationId && launch.is_primary
    );
    if (currentPrimary) {
      currentPrimary.is_primary = false;
      currentPrimary.updated_at = new Date().toISOString();
    }

    return this.create({
      location_id: input.locationId,
      status: input.status,
      installation_date: input.installationDate,
      launch_date: input.launchDate,
      installation_type: input.installationType ?? null,
      is_primary: true,
      excluded_from_upcoming_launches: input.excludedFromUpcoming ?? false,
      notes: input.notes ?? 'Restored original primary launch from location record',
    });
  },

  async hideFromUpcoming(id: string): Promise<LocationLaunch> {
    return this.update(id, { excluded_from_upcoming_launches: true });
  },
};
